import { motion } from "framer-motion";
import { MaskLines, Reveal, ClipImage } from "@/components/motion";
import { CTAButton } from "@/components/CTA";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import { BUSINESS, IMAGES, CHAPTERS } from "@/data";

const STATS = [
  { value: "300+", label: "Homes & offices cleaned" },
  { value: "4.9★", label: "Average local rating" },
  { value: "3", label: "Cities served" },
  { value: "100%", label: "Satisfaction promise" },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Us | Magical Cleaning Team — Kitchener, Waterloo & Cambridge"
        description="Meet Magical Cleaning Team — a friendly, locally-owned cleaning company serving Kitchener, Waterloo & Cambridge with reliable, detail-obsessed house and office cleaning."
        path="/about"
      />
      <section className="mx-auto max-w-[1400px] px-6 pt-36 pb-16 lg:px-10 lg:pt-48">
        <p className="overline text-[var(--brand)]">About us</p>
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.92] tracking-tighter sm:text-7xl lg:text-[6rem]">
          <MaskLines lines={["We make clean", "feel effortless."]} delay={0.1} />
        </h1>
        <Reveal delay={0.5} className="mt-8 grid gap-8 lg:grid-cols-12">
          <p className="text-lg leading-relaxed text-[var(--ink-soft)] lg:col-span-7">
            Magical Cleaning Team is a locally-owned cleaning company based in the heart of the Tri-Cities. We started
            with a simple belief: everyone deserves to come home to a space that feels calm, fresh and cared for —
            without lifting a finger. Today, our friendly, vetted team brings that feeling to hundreds of homes and
            offices across Kitchener, Waterloo and Cambridge.
          </p>
          <p className="text-base leading-relaxed text-[var(--ink-soft)] lg:col-span-4 lg:col-start-9">
            Reliable people, meticulous checklists, and a genuine love for what we do. That's the whole magic.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <ClipImage src={IMAGES.team} alt="Friendly professional cleaning team" className="aspect-[16/9] w-full" />
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-px bg-[var(--line)] sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="bg-white p-8 lg:p-10">
              <p className="font-display text-5xl font-extrabold tracking-tighter text-[var(--brand)]">{s.value}</p>
              <p className="mt-3 text-sm text-[var(--ink-soft)]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee dark />

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="max-w-2xl">
          <p className="overline text-[var(--brand)]">Our promise</p>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">What you can count on</h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-[var(--line)] lg:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1} className="bg-white p-8 lg:p-10">
              <span className="font-mono-accent text-sm text-[var(--brand)]">{c.num}</span>
              <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight">{c.title}</h3>
              <p className="mt-4 leading-relaxed text-[var(--ink-soft)]">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[var(--brand)] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10 lg:py-24">
          <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Let's make your space shine.
          </h2>
          <div className="flex flex-wrap gap-4">
            <CTAButton to="/contact" variant="dark" testId="about-quote-btn">Get Free Quote</CTAButton>
            <a href={`tel:${BUSINESS.phoneHref}`} data-testid="about-call-btn" className="sweep-btn inline-flex items-center gap-2 border border-white/40 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5">
              <span className="relative z-10">Call Us Today</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
